import React, { useState, useEffect, useRef } from 'react';
import Stats from './Stats';
import Live from './Live';
import Fixtures from './Fixtures';
import Result from './Result';
import SeasonSelector from './Seasonselector';
import Standings from './Standing';
import './App.css';
import moment from 'moment-timezone';
import * as _ from 'lodash';

// Competition URL mapping
const competitionURLMapping = {
  "national-super-league": { CompetitionID: 70, SeasonID: 114 },
  "lanka-premier-league": { CompetitionID: 55, SeasonID: 113 },
  "mens-major-club-tournament": { CompetitionID: 52, SeasonID: 113 },
  "mens-major-club-emerging": { CompetitionID: 57, SeasonID: 113 },
  "womens-super4": { CompetitionID: 5, SeasonID: 111 },
  "womens-major-club-tournament": { CompetitionID: 4, SeasonID: 111 },
  "lanka-youth-league": { CompetitionID: 6, SeasonID: 111 }
};


function App() {
  const [competitions, setCompetitions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [dropdownScrollPosition, setDropdownScrollPosition] = useState(0);
  const dropdownListRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [teamType, setTeamType] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const isWidgetTypeDomestic = process.env.REACT_APP_WIDGET_TYPE == 'domestic';
  const siteURL = process.env.REACT_APP_SITE_URL;
  const apiUrl = process.env.REACT_APP_API_URL;
  const imageURL = process.env.REACT_APP_IMAGE_URL;
  const defaultPlayerImage = `${imageURL}player-thumb.jpg`;
  const defaultTeamImage = `${imageURL}default-team-logo.png`;
  const handleImageError = (e) => {
    e.target.src = defaultPlayerImage;
  };
  const handleTeamImageError = (e) => {
    e.target.src = defaultTeamImage;
  };
  // Save scroll position when dropdown is open
  useEffect(() => {
    if (isOpen && dropdownListRef.current) {
      dropdownListRef.current.scrollTop = dropdownScrollPosition;
    }
  }, [isOpen, dropdownScrollPosition]);
  // Handle scroll in dropdown
  const handleDropdownScroll = (event) => {
    setDropdownScrollPosition(event.target.scrollTop);
  };

  // Add this function to get team type from URL path
  const getTeamTypeFromURL = () => {
    const pathname = window.location.pathname;

    // Extract the last part of the path
    const pathSegments = pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Match the path segment to team types
    switch (lastSegment) {
      case 'sri-lanka-men':
        return "Mens Senior";
      case 'sri-lanka-women':
        return "Womens Senior";
      case 'under-19':
        return "U19";
      case 'emerging':
        return "Emerging";
      default:
        return "";
    }
  };

  const getCompetitionFromURL = () => {
    const pathname = window.location.pathname;

    // Check if URL contains "other"
    if (pathname.includes("other")) {
      return { useFirstCompetition: true };
    }

    const urlKey = Object.keys(competitionURLMapping).find(key => pathname.includes(key));
    return urlKey ? { ...competitionURLMapping[urlKey], urlKey } : null;
  };

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`${apiUrl}competition.js?t=${timestamp}`);
        let text = await response.text();
        text = text.trim();
        let matches = text.match(/\[\{.*?\}\]/g);
        if (!matches) {
          throw new Error('Invalid data format');
        }

        let competitionData = matches.find((match) => match.includes("CompetitionName"));
        let divisionData = matches.find((match) => match.includes("DivisionName"));
        if (!competitionData || !divisionData) {
          throw new Error("Competition data not found");
        }

        const parsedData = JSON.parse(competitionData);
        const parsedDivisions = JSON.parse(divisionData);
        const currentTeamType = getTeamTypeFromURL();
        setTeamType(currentTeamType);

        // Get URL-based competition filter
        const urlCompetition = getCompetitionFromURL();

        // Filter competitions by team type if specified
        const filteredData = currentTeamType
          ? parsedData.filter(comp => comp.TeamType === currentTeamType)
          : parsedData;


        if (isWidgetTypeDomestic) {
          // Get all seasons
          const allSeasons = _.uniqBy(parsedDivisions, 'SeasonID')
            .map(div => ({
              SeasonID: div.SeasonID,
              SeasonName: div.SeasonName
            }))
            .sort((a, b) => b.SeasonID - a.SeasonID);

          setSeasons(allSeasons);
          // Set initial season based on URL mapping or default to first season
          let initialSeason;
          if (urlCompetition) {
            initialSeason = allSeasons.find(season => season.SeasonID == urlCompetition.SeasonID) || allSeasons[0];
          } else {
            initialSeason = allSeasons[0];
          }
          setSelectedSeason(initialSeason);

          // Get divisions for selected season
          const seasonDivisions = parsedDivisions.filter(div =>
            div.SeasonID == initialSeason.SeasonID
          );

          // Get all competitions for selected season
          const seasonCompetitions = filteredData.filter(comp => {
            const matchingDivision = seasonDivisions.find(div =>
              div.DivisionID == comp.DivisionID
            );
            return matchingDivision && comp.SeasonID === initialSeason.SeasonID;
          });

          setCompetitions(seasonCompetitions);

          // Set default competition based on URL mapping if it exists and matches season
          if (urlCompetition) {
            const defaultComp = seasonCompetitions.find(comp =>
              comp.CompetitionID === urlCompetition.CompetitionID &&
              comp.SeasonID === urlCompetition.SeasonID
            );
            if (defaultComp) {
              setSelectedCompetition(defaultComp);
            } else if (seasonCompetitions.length > 0) {
              setSelectedCompetition(seasonCompetitions[0]);
            }
          } else if (seasonCompetitions.length > 0) {
            setSelectedCompetition(seasonCompetitions[0]);
          }
        } else {
          const uniqueCompetitions = filteredData.reduce((acc, curr) => {
            if (!acc.some(comp => comp.CompetitionID === curr.CompetitionID)) {
              acc.push(curr);
            }
            return acc;
          }, []);

          setCompetitions(uniqueCompetitions);
          if (uniqueCompetitions.length > 0) {
            setSelectedCompetition(uniqueCompetitions[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching competitions:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompetitions();

    // Listen for URL changes
    const handleURLChange = () => {
      const newTeamType = getTeamTypeFromURL();
      if (newTeamType !== teamType) {
        setTeamType(newTeamType);
      }
      fetchCompetitions();
    };

    window.addEventListener('popstate', handleURLChange);
    return () => window.removeEventListener('popstate', handleURLChange);
  }, [apiUrl, isWidgetTypeDomestic, teamType]);

  const handleSeasonSelect = async (season) => {
    setSelectedSeason(season);

    if (isWidgetTypeDomestic) {
      try {
        const response = await fetch(`${apiUrl}competition.js`);
        const text = await response.text();
        const matches = text.match(/\[\{.*?\}\]/g);

        if (matches) {
          const competitionData = matches.find(match => match.includes("CompetitionName"));
          const divisionData = matches.find(match => match.includes("DivisionName"));

          if (competitionData && divisionData) {
            const parsedCompetitions = JSON.parse(competitionData);
            const parsedDivisions = JSON.parse(divisionData);
            const currentTeamType = getTeamTypeFromURL();

            // Filter by team type if needed
            let filteredCompetitions = currentTeamType
              ? parsedCompetitions.filter(comp => comp.TeamType === currentTeamType)
              : parsedCompetitions;

            // Get divisions for selected season
            const seasonDivisions = parsedDivisions.filter(div =>
              div.SeasonID === season.SeasonID
            );

            // Get all competitions for selected season
            const seasonCompetitions = filteredCompetitions.filter(comp => {
              const matchingDivision = seasonDivisions.find(div =>
                div.DivisionID === comp.DivisionID
              );
              return matchingDivision && comp.SeasonID === season.SeasonID;
            });

            setCompetitions(seasonCompetitions);

            // Check if current competition is valid for new season
            const currentCompStillValid = seasonCompetitions.find(
              comp => comp.CompetitionID === selectedCompetition?.CompetitionID
            );

            if (currentCompStillValid) {
              // Keep current competition if it exists in new season
              setSelectedCompetition(currentCompStillValid);
            } else if (seasonCompetitions.length > 0) {
              // Otherwise select first available competition
              setSelectedCompetition(seasonCompetitions[0]);
              setActiveTab(""); // Reset active tab for new competition
            }
          }
        }
      } catch (error) {
        console.error("Error updating competitions for season:", error);
      }
    }
  };


  const filteredCompetitions = competitions.filter(comp => {
    const matchesSearch = comp.CompetitionName?.toLowerCase().includes(searchText.toLowerCase());
    const matchesTeamType = !teamType || comp.TeamType === teamType;
    return matchesSearch && matchesTeamType;
  });
  const handleCompetitionSelect = (competition) => {
    // Store current scroll position
    const scrollPosition = window.scrollY;

    // Update competition and close dropdown
    setActiveTab("");
    setSelectedCompetition(competition);
    setIsOpen(false);

    // Restore scroll position after state updates
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('competitionBox');
      if (dropdown && !dropdown.contains(event.target)) {
        // setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    const fetchMatches = async () => {
      try {
        if (!selectedCompetition) return;
        setIsLoading(true);
        const timestamp = new Date().getTime();
        const matchResponse = await fetch(
          `${selectedCompetition.feedsource}/${selectedCompetition.CompetitionID}-matchschedule.js?t=${timestamp}`
        );
        const matchText = await matchResponse.text();
        const matchMatch = matchText.match(/\[\{.*?\}\]/);
        if (!matchMatch) throw new Error("Invalid match data");
        const parsedMatches = JSON.parse(matchMatch[0]);

        console.log(selectedCompetition);
        // Filter matches for the selected competition
        const filteredMatches = parsedMatches.filter(
          match => match.CompetitionID == selectedCompetition.CompetitionID
        );
        console.log(filteredMatches);
        // Determine if there are any live matches
        const hasLive = filteredMatches.some(match => match.MatchStatus === "Live");
        const hasUpcoming = filteredMatches.some(match => match.MatchStatus === "UpComing" || match.MatchStatus === "Upcoming");
        const hasCompleted = filteredMatches.some(match => match.MatchStatus === "Post");

        console.log(hasCompleted, activeTab);
        // Set tab based on available matches, preserving user selection if valid
        if (hasLive && (activeTab === "LIVE" || !activeTab)) {
          console.log("live");
          setActiveTab("LIVE");
        } else if (hasUpcoming && (activeTab === "FIXTURES" || !activeTab)) {
          console.log("FIXTURES");
          setActiveTab("FIXTURES");
        } else if (hasCompleted && (activeTab === "RESULTS" || !activeTab)) {
          console.log("RESULTS");
          setActiveTab("RESULTS");
        } else if (hasCompleted && (activeTab === "STATS")) {
          setActiveTab("STATS");
        } else if (isWidgetTypeDomestic && hasCompleted && (activeTab === "STANDINGS" || !activeTab)) {
          setActiveTab("STANDINGS");
        } else if (!hasLive && !hasUpcoming && !hasCompleted) {
          console.log("FIXTURES");
          setActiveTab("FIXTURES");
        }

        setMatches(parsedMatches);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();

    let refreshInterval;
    if (activeTab === "LIVE") {
      refreshInterval = setInterval(fetchMatches, 10000);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [selectedCompetition, activeTab]);


  // Update the updateActiveTab function to use the new filtering logic
  const updateActiveTab = (matches) => {
    let filteredMatches = matches.filter(
      match => match.CompetitionID === selectedCompetition.CompetitionID
    );

    // Apply team filter if it exists
    if (selectedCompetition?.filterteam) {
      filteredMatches = filteredMatches.filter(match => {
        return [
          match.FirstBattingTeamID,
          match.SecondBattingTeamID,
          match.AwayTeamID,
          match.HomeTeamID
        ].includes(parseInt(selectedCompetition.filterteam))
      });
    }

    if (filteredMatches.some((match) => match.MatchStatus === "Live")) {
      setActiveTab("LIVE");
    } else if (filteredMatches.some((match) =>
      match.MatchStatus === "UpComing" || match.MatchStatus === "Upcoming"
    )) {
      setActiveTab("FIXTURES");
    } else if (filteredMatches.some((match) => match.MatchStatus === "Post")) {
      setActiveTab("RESULTS");
    } else {
      setActiveTab("STATS");
    }
  };


  const getFilteredMatches = () => {

    if (!selectedCompetition || matches.length === 0) return [];

    // First filter matches by competition ID
    let filteredMatches = matches.filter(match =>
      match.CompetitionID == selectedCompetition.CompetitionID
    );

    // Then filter by team ID if filterteam exists in selectedCompetition
    if (selectedCompetition.filterteam) {
      filteredMatches = filteredMatches.filter(match => {
        // Check if filterteam matches any of the team IDs
        return [
          match.FirstBattingTeamID,
          match.SecondBattingTeamID,
          match.AwayTeamID,
          match.HomeTeamID
        ].includes(parseInt(selectedCompetition.filterteam))
      });
    }

    // Then filter by match status based on active tab
    switch (activeTab) {
      case "LIVE":
        return filteredMatches.filter((match) => match.MatchStatus === "Live");
      case "FIXTURES":
        return filteredMatches.filter((match) =>
          match.MatchStatus === "UpComing" || match.MatchStatus === "Upcoming"
        );
      case "RESULTS":
        return filteredMatches.filter((match) => match.MatchStatus === "Post");
      default:
        return [];
    }
  };


  // Update the hasLiveMatches, hasUpcomingMatches, and hasCompletedMatches checks
  const hasLiveMatches = matches.some(match => {
    const teamMatches = selectedCompetition?.filterteam ?
      [match.FirstBattingTeamID, match.SecondBattingTeamID, match.AwayTeamID, match.HomeTeamID]
        .includes(parseInt(selectedCompetition.filterteam)) : true;
    return match.CompetitionID == selectedCompetition?.CompetitionID &&
      match.MatchStatus == "Live" && teamMatches;
  });

  const hasUpcomingMatches = matches.some(match => {
    const teamMatches = selectedCompetition?.filterteam ?
      [match.FirstBattingTeamID, match.SecondBattingTeamID, match.AwayTeamID, match.HomeTeamID]
        .includes(parseInt(selectedCompetition.filterteam)) : true;
    return match.CompetitionID == selectedCompetition?.CompetitionID &&
      (match.MatchStatus == "UpComing" || match.MatchStatus == "Upcoming") &&
      teamMatches;
  });

  const hasCompletedMatches = matches.some(match => {
    const teamMatches = selectedCompetition?.filterteam ?
      [match.FirstBattingTeamID, match.SecondBattingTeamID, match.AwayTeamID, match.HomeTeamID]
        .includes(parseInt(selectedCompetition.filterteam)) : true;
    return match.CompetitionID == selectedCompetition?.CompetitionID &&
      match.MatchStatus === "Post" &&
      teamMatches;
  });

  const filteredMatches = getFilteredMatches();
  const formatMatchTime = (GMTMatchDate, GMTMatchTime, MatchDate) => {
    if (!GMTMatchDate && !GMTMatchTime && MatchDate) {
      // If GMT values are null but MatchDate exists, show only MatchDate
      return moment(MatchDate).format('DD MMM YYYY');
    }

    if (!GMTMatchDate || !GMTMatchTime) {
      return MatchDate ? moment(MatchDate).format('DD MMM YYYY') : '';
    }

    const gmtTime = moment.utc(`${GMTMatchDate} ${GMTMatchTime}`, "YYYY-MM-DD HH:mm");
    const istTime = gmtTime.clone().tz('Asia/Kolkata');
    return `${istTime.format('DD MMM YYYY hh:mm A')} IST`;
  };
  const shouldDisplayWicket = (wicket) => {
    return (wicket != 10);
  };

  return (
    <div className="smscorecardwidget matchCenter  smWebview" >
      <div id="mcWrapper">
        {/* <div className="mcLoader pageloader"> <div className="loader__inner">Loading... </div> </div> */}
        <div className="mcRow active" id="matchCenter">
          <div className="matchHeaderWrapper sm-flex sm-justify-between sm-align-center">
            <div id="matchFilterWrapper" className="cSB">

              <div className="seasonFilter">
                {isWidgetTypeDomestic && (
                  <SeasonSelector
                    seasons={seasons}
                    selectedSeason={selectedSeason}
                    onSeasonSelect={handleSeasonSelect}
                  />
                )}
                <div id="competitionBox" className="seasonFilter__iteam" >
                  <div className={`customSelecBox2 ${isOpen ? 'openSBox' : ''}`}>
                    <div className="cSBDisplay " onClick={() => setIsOpen(!isOpen)}>
                      {selectedCompetition?.CompetitionName || "Select a competition"}</div>
                    {isOpen && (
                      <div className={`cSBList2 ${isOpen ? 'active' : ''}`} ref={dropdownListRef}
                        onScroll={handleDropdownScroll}>
                        <div className="cSBList2Search" >
                          <input type="text" placeholder="Search.." className=""
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                        </div>
                        {filteredCompetitions.map((comp) => (
                          <div
                            key={comp.CompetitionID}
                            className="cSBList2Items"
                            onClick={() => handleCompetitionSelect(comp)}
                          >
                            {comp.CompetitionName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div id="mcMenuWrapper">
              <div className="tabLinkscontainer">
                <ul className="mcTabs">
                  {hasLiveMatches && (
                    <li
                      className={activeTab === "LIVE" ? "current" : ""}
                      onClick={() => setActiveTab("LIVE")}
                    >
                      <span>Live</span>
                    </li>
                  )}
                  {hasUpcomingMatches && (
                    <li
                      className={activeTab === "FIXTURES" ? "current" : ""}
                      onClick={() => setActiveTab("FIXTURES")}
                    >
                      <span>Fixtures</span>
                    </li>
                  )}
                  {hasCompletedMatches && (
                    <>
                      <li
                        className={activeTab === "RESULTS" ? "current" : ""}
                        onClick={() => setActiveTab("RESULTS")}
                      >
                        <span>Results</span>
                      </li>
                      <li className={activeTab === "STATS" ? "current" : ""}
                        onClick={() => setActiveTab("STATS")}>
                        <span>Stats</span>
                      </li>
                      {isWidgetTypeDomestic && (
                        <li
                          className={activeTab === "STANDINGS" ? "current" : ""}
                          onClick={() => setActiveTab("STANDINGS")}
                        >
                          <span>Standings</span>
                        </li>
                      )}
                    </>
                  )}

                </ul>
              </div>
            </div>
          </div>

          <div className="matchContentWrapper">
            {activeTab === "STATS" ? (
              <Stats competitionId={selectedCompetition?.CompetitionID}
                statsFeed={selectedCompetition?.statsFeed} />
            ) : activeTab === "STANDINGS" ? (
              <Standings
                competitionId={selectedCompetition?.CompetitionID}
                statsFeed={selectedCompetition?.statsFeed}
              />
            ) : (
              <div className="mcContainer">

                <div className="matchSearch col s12 m12 l4" >
                  <input type="search" className="" />
                </div>
                {filteredMatches.length === 0 ? (
                  <div className="">
                    <span className="noRecord">No Matches Found... </span>
                  </div>
                ) : (
                  filteredMatches.map((match) => (
                    activeTab === "LIVE" ? (
                      <Live key={match.MatchID}
                        match={match}
                        selectedCompetition={selectedCompetition}
                        matchDate={match.MatchDateNew}
                        matchTimeGMT={match.GMTMatchTime}
                      />
                    ) : activeTab === "FIXTURES" ? (
                      <Fixtures key={match.MatchID}
                        selectedCompetition={selectedCompetition}
                        match={match}
                        matchDate={match.GMTMatchDate}
                        matchTimeGMT={match.GMTMatchTime}
                      />
                    ) : (

                      <Result
                        key={match.MatchID}
                        match={match}
                        selectedCompetition={selectedCompetition}
                        handleTeamImageError={handleTeamImageError}
                        defaultTeamImage={defaultTeamImage}
                        siteURL={siteURL}
                      />
                    )
                  )
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div >
  );
}

export default App;
